import { FontSize, LocalStorageKeys, RETURN_REASONS } from 'commons';
import { Accordion, Button, Product, Text } from 'components';
import { useFormik } from 'formik';
import { parseToReturnEntry, setLocalStorageItem } from 'helpers';
import { useAuth } from 'hooks';
import {
  ConfirmInconsistencyModal,
  ConfirmReturnModal,
} from 'pages/exchange-and-return/components';
import { useState } from 'react';
import { MdClose } from 'react-icons/md';
import { toast } from 'react-toastify';
import { exchangeAndReturnService } from 'services';
import { ReturnDetails, ReturnRequestAction } from 'types';
import { ReturnConsignmentsListStore } from 'zustand-store';
import * as S from './styles';

interface Props {
  returnDetails: ReturnDetails;
  isReceivedByCustomerInPostOfficeFlow: boolean;
}

interface FormValues {
  description: string;
  action: ReturnRequestAction;
}

export function ConfirmReceived({
  returnDetails,
  isReceivedByCustomerInPostOfficeFlow,
}: Props) {
  const { mutate: mutateReturnConsignmentList } =
    ReturnConsignmentsListStore.useGetReturnConsignments();
  const { protocol, entries, consignmentCode } = returnDetails;
  const { selectedStore, user } = useAuth();
  const { isAdmin } = user;
  const { name: store } = selectedStore;

  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  function openModal() {
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
  }

  const defaultAction = isReceivedByCustomerInPostOfficeFlow
    ? ReturnRequestAction.ReceivedByCustomerInPostOfficeFlow
    : ReturnRequestAction.Received;

  const {
    values,
    handleChange,
    setFieldValue,
    handleSubmit,
    handleReset,
    submitForm,
  } = useFormik<FormValues>({
    initialValues: {
      description: '',
      action: defaultAction,
    },

    onSubmit: async ({ action, description }) => {
      async function submitData() {
        setIsSubmitting(true);

        const result = await exchangeAndReturnService.postConfirmReceipt({
          protocol,
          action,
          description: description || undefined,
        });

        const isInconsistency = action === ReturnRequestAction.Inconsistency;

        if (isInconsistency) {
          const supportLink = `https://api.whatsapp.com/send?phone=+555121295354&text=Retire%20e%20Entrega%20-%20Loja%20*${store}*%20Sobre%20a%20devolução%20do%20pedido%20*${consignmentCode}*`;
          window.open(supportLink, '_blank');
        }

        if (result) {
          const toastMessage = isInconsistency
            ? 'A inconsistência foi relatada com sucesso'
            : 'Devolução realizada com sucesso';

          toast.success(toastMessage);

          setLocalStorageItem(
            `${LocalStorageKeys.ConfirmExchangeAndReturnReceived}_${protocol}` as any,
            { protocol: protocol, action: action }
          );

          mutateReturnConsignmentList();
        } else {
          setIsSubmitting(false);
        }

        closeModal();
      }

      if (showModal) {
        submitData();
      } else {
        openModal();
      }
    },
  });

  const renderEntries = entries.map(
    ({ entry, reason, additionalReason, additionalInformation }, index) => (
      <S.ProductWrapper key={index}>
        <S.ReturnProductWrapper>
          <Product entry={parseToReturnEntry(entry)} clearBorder />
        </S.ReturnProductWrapper>

        <S.ReasonWrapper>
          <S.TextWrapper>
            <Text size={FontSize.font12}>Razão da devolução</Text>

            <S.DisplayText>
              <Text size={FontSize.font12}>{RETURN_REASONS[reason]}</Text>
            </S.DisplayText>
          </S.TextWrapper>

          <S.TextWrapper>
            <Text size={FontSize.font12}>Mais detalhes</Text>

            <S.DisplayText>
              <Text size={FontSize.font12}>
                {RETURN_REASONS[additionalReason]}
              </Text>
            </S.DisplayText>
          </S.TextWrapper>

          <S.TextWrapper>
            <Text size={FontSize.font12}>Informações adicionais</Text>

            <S.DisplayText>
              <Text size={FontSize.font12}>{additionalInformation}</Text>
            </S.DisplayText>
          </S.TextWrapper>
        </S.ReasonWrapper>
      </S.ProductWrapper>
    )
  );

  const isInconsistency = values.action === ReturnRequestAction.Inconsistency;

  return (
    <Accordion title="Confirmação de recebimento" defaultOpen>
      <S.Form onSubmit={handleSubmit} onReset={handleReset}>
        {showModal &&
          (isInconsistency ? (
            <ConfirmInconsistencyModal
              closeModal={closeModal}
              submitForm={submitForm}
              isSubmitting={isSubmitting}
            />
          ) : (
            <ConfirmReturnModal
              closeModal={closeModal}
              submitForm={submitForm}
              isSubmitting={isSubmitting}
            />
          ))}

        <Text size={FontSize.font12}>
          Acompanhe o histórico da devolução e o status do correio. Quando o
          produto chegar confirme se era o mesmo produto cujo a devolução foi
          solicitada e confirme o recebimento.
        </Text>

        {renderEntries}

        {isInconsistency && (
          <S.TextWrapper>
            <Text size={FontSize.font12}>Descreva qual a inconsistência</Text>

            <S.TextArea
              id="description"
              name="description"
              onChange={handleChange}
              placeholder="Escreva o que foi entrega na loja"
              maxLength={255}
              disabled={isAdmin}
            />
          </S.TextWrapper>
        )}

        <S.ButtonsWrapper>
          {isInconsistency ? (
            <>
              <Button
                type="reset"
                variant="transparent"
                disabled={isSubmitting || isAdmin}
              >
                <S.Icon>
                  <MdClose />
                </S.Icon>
                Cancelar
              </Button>

              <Button
                type="submit"
                variant="danger"
                isLoading={
                  isSubmitting &&
                  values.action === ReturnRequestAction.Inconsistency
                }
                disabled={isAdmin}
              >
                Salvar inconsistência
              </Button>
            </>
          ) : (
            isReceivedByCustomerInPostOfficeFlow || (
              <Button
                type="button"
                variant="outline-secondary"
                disabled={isSubmitting || isAdmin}
                onClick={() =>
                  setFieldValue('action', ReturnRequestAction.Inconsistency)
                }
              >
                Relatar inconsistência
              </Button>
            )
          )}

          <Button
            type="submit"
            disabled={isInconsistency || isAdmin}
            isLoading={isSubmitting && values.action === defaultAction}
          >
            Confirmar recebimento
          </Button>
        </S.ButtonsWrapper>
      </S.Form>
    </Accordion>
  );
}
